import { FormField, RadioButton, FormFieldRadioButtonGroup } from '@vwfs-bronson/bronson-react';
import { useField } from 'formik';
import React, { FormEventHandler, MouseEventHandler } from 'react';
import { InfoIcon } from '../../info-icon';
import { getInputErrorMessageComponentAndId } from '../errors';

type RadioButton = { label: string; value: string | number | readonly string[] | undefined; tooltip?: string };

type ValidatedRadioButtonGroupProps = {
    radioButtons: RadioButton[];
    name: string;
    label?: string;
    ariaLabel?: string;
    tooltip?: string;
    defaultChecked?: boolean;
    disabled?: boolean;
    testId?: string;
    handleChange?: FormEventHandler<HTMLElement>;
    className?: string;
    isMandatory?: boolean;
    withHtmlValidationWarning?: boolean;
    formFieldClassName?: string;
    hint?: string;
    id?: string;
    onLinkClick?: MouseEventHandler;
    required?: boolean;
};

export const ValidatedRadioButtonGroup: React.FC<ValidatedRadioButtonGroupProps> = ({
    radioButtons,
    name,
    tooltip,
    defaultChecked,
    label,
    ariaLabel,
    disabled,
    testId,
    handleChange,
    className,
    isMandatory = false,
    withHtmlValidationWarning = false,
    formFieldClassName,
    hint,
    id,
    onLinkClick,
    required,
}) => {
    const [field, meta] = useField(name);

    const infoIcon = tooltip ? <InfoIcon text={tooltip} /> : null;

    const { errorMessageComponent, errorMessageId } = getInputErrorMessageComponentAndId({
        name,
        id,
        meta,
        testId,
        withHtmlValidationWarning,
    });

    return (
        <FormField
            type="other"
            id={id}
            testId={`${testId}FormField`}
            errorMessage={errorMessageComponent}
            labelText={label}
            infoIcon={infoIcon}
            onChange={handleChange}
            required={isMandatory}
            onClick={onLinkClick}
            className={formFieldClassName}
            hint={hint}
        >
            <FormFieldRadioButtonGroup
                className={className}
                aria-label={ariaLabel || label}
                aria-describedby={errorMessageId}
                {...field}
            >
                {radioButtons.map(({ label, value, tooltip }, index: number) => (
                    <RadioButton
                        key={index}
                        testId={`${testId}.${value}`}
                        name={name}
                        value={value}
                        disabled={disabled}
                        defaultChecked={defaultChecked || value === field.value}
                        aria-label={label}
                        required={required}
                    >
                        {label}
                        {tooltip && <InfoIcon text={tooltip} />}
                    </RadioButton>
                ))}
            </FormFieldRadioButtonGroup>
        </FormField>
    );
};
